import { ASSET_LABELS_CRUD_ACTION_CREATORS } from '../actions/asset_labels_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import { FILTER_CONFIG_NAMES, ROLE_TYPES } from '../utils/DataConstants';
import {
	createLabelCatalogueSyncThunk,
	getDeletedLabelIds,
	LabelCatalogueEntry,
} from './label_catalogue_sync';
import { nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { buildLabelFilterRemovalPatch } from '../utils/FilterUtils';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { applyAssetsFiltersForRole } from './assets_thunks';
import { ASSETS_CRUD_ACTION_CREATORS } from '../actions/assets_actions';
import { getRecordsForTargetCollection } from '../reducers/standard_reducer_utils';
import { LabelEntityType } from '../types/labels';

export const labelsOnAssetsRestCrudThunksForSupplier = new RestCrudThunks(
	'asset_labels',
	'label',
	'label',
	'asset_labels',
	ASSET_LABELS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'asset/'
);

export const labelsOnAssetsRestCrudThunksForBuyer = new RestCrudThunks(
	'asset_labels',
	'label',
	'label',
	'asset_labels',
	ASSET_LABELS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'asset/'
);

export const labelsOnAssetsRestCrudThunks = (userType: string) =>
	userType === ROLE_TYPES.SUPPLIER
		? labelsOnAssetsRestCrudThunksForSupplier
		: labelsOnAssetsRestCrudThunksForBuyer;

const fetchUtils = new FetchUtils();

export const associateAssetLabelsWithEntity =
	(roleType: string) => (entityId: number, labelIds: number[]) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/asset/assets/${entityId}/labels`;

		return (dispatch, getState) => {
			return new Promise<any>((resolve, reject) => {
				fetchUtils
					.put(baseUrl + apiUrl, {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: { ids: labelIds },
					})
					.then(parseJSON)
					.then(({ json, status }) => {
						const responseBody = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (responseBody.status === 'error') {
							reject(responseBody.message);
						} else {
							// Patch rather than readOne: readOne creates a `records[id]` entry, which shadows
							// the `searchRecords` the OpenSearch-backed assets list writes.
							// Resolve from the slice's `records` map, not a target collection — the Add Label
							// modal loads into `assetLabelsDropdown`, so reading any single collection can
							// come back empty and blank the asset's chips.
							const labelsById = nullSafeGetOrElse('records', getState().asset_labels, {});
							dispatch(
								ASSETS_CRUD_ACTION_CREATORS.updatePartialSuccess({
									id: entityId,
									assetLabels: (labelIds || []).map((id) => labelsById[id]).filter(Boolean),
								})
							);
							resolve(responseBody.data);
						}
					})
					.catch((error) => {
						console.error('Error associating asset labels:', error);
						reject(error);
					});
			});
		};
	};

// Cached assets keep their own copy of the labels applied to them, so a deleted label lingers
// on the row until a page reload. Drop it from every cached record.
export const removeDeletedLabelsFromAssets =
	(deletedLabelIds: number[]) => (dispatch, getState) => {
		const deleted = new Set(deletedLabelIds.map(Number));
		const slice = getState().assets;
		const patched = new Set();
		[slice.records, slice.searchRecords].forEach((recordMap) => {
			Object.keys(recordMap || {}).forEach((recordId) => {
				if (patched.has(recordId)) return;
				const labels = recordMap[recordId] && recordMap[recordId].assetLabels;
				if (!Array.isArray(labels)) return;
				const kept = labels.filter((label) => !deleted.has(Number(label && label.id)));
				if (kept.length === labels.length) return;
				patched.add(recordId);
				dispatch(
					ASSETS_CRUD_ACTION_CREATORS.updatePartialSuccess({
						id: Number(recordId),
						assetLabels: kept,
					})
				);
			});
		});
	};

const baseSyncAssetLabelsForRole = createLabelCatalogueSyncThunk(
	(roleType) => `/api/v1/${roleType}/asset/asset_labels/sync`,
	'Failed to save labels. Please try again.',
	{
		getCatalogue: (state) =>
			getRecordsForTargetCollection(state.asset_labels, LabelEntityType.ASSET),
		onDeletedLabels: removeDeletedLabelsFromAssets,
	}
);

// Deleting a label must also unpin it from an active Labels filter, or the list stays filtered
// on an id that matches nothing. applyAssetsFiltersForRole re-syncs the URL too.
const syncAssetLabelsForRole =
	(roleType: string) =>
	(labels: LabelCatalogueEntry[], context: { history?: any; targetCollectionName?: string } = {}) =>
	(dispatch, getState) => {
		const catalogueBeforeSync = getRecordsForTargetCollection(
			getState().asset_labels,
			LabelEntityType.ASSET
		);
		return dispatch(baseSyncAssetLabelsForRole(roleType)(labels)).then((result) => {
			const { history, targetCollectionName } = context;
			if (!history || !targetCollectionName) return result;
			const deletedLabelIds = getDeletedLabelIds(catalogueBeforeSync, labels);
			const filters = nullSafeGetOrElse(`${targetCollectionName}.filters`, getState().assets, {});
			const patch = buildLabelFilterRemovalPatch(
				filters,
				deletedLabelIds,
				FILTER_CONFIG_NAMES.LABEL_IDS
			);
			if (patch) {
				dispatch(applyAssetsFiltersForRole(roleType)(history, targetCollectionName, patch));
			}
			return result;
		});
	};

export const syncAssetLabelsForBuyer = syncAssetLabelsForRole(ROLE_TYPES.BUYER);
export const syncAssetLabelsForSupplier = syncAssetLabelsForRole(ROLE_TYPES.SUPPLIER);

export const syncAssetLabels = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? syncAssetLabelsForSupplier : syncAssetLabelsForBuyer;
